import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container } from "reactstrap";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaBookOpen, FaGem, FaPenNib, FaCalendarAlt, FaUser, FaArrowRight } from "react-icons/fa";
import { getApiUrl, getMediaUrl, siteConfig } from "../utils/site";
import blogHeroImage from "../assets/all-images/bg13.jpg";
import "../styles/luxury-pages.css";
import "../styles/world-class-luxury.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65 },
  },
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const blogTopics = [
  "Ethiopian Opal",
  "Luxury Gemstones",
  "Jewelry Care",
  "Custom Jewelry",
  "Habesha Gold",
  "Ethiopian Heritage",
  "Gemstone Mining",
  "Design Process",
  "Luxury Trends",
  "Craftsmanship"
];

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(getApiUrl("/api/blogs"))
      .then((r) => setBlogs(r.data.blogs || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Roha Jewellery Journal | Ethiopian Gemstone and Luxury Jewellery Stories
        </title>

        <meta
          name="description"
          content="Discover Ethiopian gemstone jewellery stories, opal guides, luxury jewellery inspiration, custom jewellery design, jewellery care tips, and premium Ethiopian jewellery craftsmanship from Roha Jewellery."
        />

        <meta
          name="keywords"
          content="Ethiopian gemstone jewellery, Ethiopian opal jewellery, luxury jewellery Ethiopia, Habesha jewellery, gemstone guide, jewellery blog Ethiopia, Roha Jewellery"
        />

        <meta name="author" content="Roha Jewellery" />

        <link rel="canonical" href={`${siteConfig.url}/blogs`} />

        <meta
          property="og:title"
          content="Roha Jewellery Journal | Ethiopian Luxury Gemstone Jewellery"
        />

        <meta
          property="og:description"
          content="Explore Ethiopian gemstone stories, luxury jewelry inspiration, opal guides, custom jewelry design, and fine jewelry craftsmanship from Roha Jewellery."
        />

        <meta
          property="og:image"
          content={`${siteConfig.url}/images/blog-cover.jpg`}
        />

        <meta property="og:url" content={`${siteConfig.url}/blogs`} />

        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Roha Jewellery Blog | Ethiopian Gemstone Jewelry"
        />

        <meta
          name="twitter:description"
          content="Luxury Ethiopian gemstone jewelry stories, opal guides, jewelry inspiration, and handcrafted design articles."
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Roha Jewellery Journal",
            url: `${siteConfig.url}/blogs`,
            publisher: {
              "@type": "Organization",
              name: "Roha Jewellery",
              url: siteConfig.url,
            },
          })}
        </script>
      </Helmet>

      <main className="luxury-page">
        {/* Hero Section */}
        <section className="luxury-page__hero">
          <div className="luxury-page__hero-bg">
            <img src={blogHeroImage} alt="Roha Jewellery Journal Background" />
          </div>
          <div className="luxury-page__hero-overlay"></div>
          <div className="luxury-page__hero-gradient"></div>
          
          <Container>
            <div className="luxury-page__hero-content">
              <div className="luxury-page__hero-eyebrow">
                <FaBookOpen /> Ethiopian Luxury Jewellery Journal
              </div>
              
              <h1 className="luxury-page__hero-title">
                Ethiopian <span className="luxury-page__hero-title-gradient">Gemstone Stories</span> for a Global Luxury Audience
              </h1>
              
              <p className="luxury-page__hero-subtitle">
                Discover Ethiopian opal jewellery, luxury gemstone craftsmanship, custom jewellery inspiration, 
                and premium handcrafted jewellery stories from Roha Jewellery. Explore the art, science, and heritage 
                behind each exquisite piece.
              </p>
              
              <div className="luxury-page__hero-actions">
                <Link to="/products" className="wc-button wc-button-primary wc-button-lg">
                  Explore Collection
                </Link>
                <Link to="/design-request" className="wc-button wc-button-secondary">
                  Custom Design
                </Link>
              </div>
              
              <div className="luxury-page__hero-tags">
                {blogTopics.slice(0, 6).map((t) => (
                  <span key={t} className="luxury-page__hero-tag">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Blog Grid Section */}
        <section className="luxury-section luxury-section--soft">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaPenNib /> Latest Articles
              </div>
              <h2 className="luxury-section__title">Premium Gemstone Jewellery Stories</h2>
              <p className="luxury-section__subtitle">
                Expert insights, gemstone guides, and luxury jewellery inspiration from Ethiopia's premier jewellery house.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-gold" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-secondary">Loading articles...</p>
              </div>
            ) : blogs.length === 0 ? (
              <div className="text-center py-5">
                <FaBookOpen size={64} className="text-muted mb-4" />
                <h4 className="text-secondary mb-3">New Ethiopian luxury jewellery articles coming soon</h4>
                <p className="text-muted">Our editorial team is preparing insightful content about gemstones and craftsmanship.</p>
              </div>
            ) : (
              <motion.div
                className="luxury-grid luxury-grid--3"
                initial="hidden"
                animate="visible"
                variants={stagger}
              >
                {blogs.map((blog) => (
                  <motion.article
                    key={blog.id}
                    className="luxury-card"
                    variants={fadeUp}
                  >
                    <div className="luxury-card__image-wrapper">
                      <img
                        src={
                          blog.image_url
                            ? getMediaUrl(blog.image_url)
                            : "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                        }
                        alt={blog.title}
                        className="luxury-card__image"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                        }}
                      />
                      <div className="luxury-card__overlay"></div>
                      <div className="luxury-card__badge">
                        <FaGem /> Article
                      </div>
                    </div>
                    
                    <div className="luxury-card__body">
                      <div className="luxury-card__meta">
                        <div className="luxury-card__meta-item">
                          <FaCalendarAlt /> {blog.created_at
                            ? new Date(blog.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "Recent"}
                        </div>
                        <div className="luxury-card__meta-item">
                          <FaUser /> {blog.author || "Roha Jewellery"}
                        </div>
                      </div>
                      
                      <h3 className="luxury-card__title">
                        <Link to={`/blogs/${blog.id}`}>
                          {blog.title}
                        </Link>
                      </h3>
                      
                      <p className="luxury-card__description">
                        {String(blog.content || "")
                          .replace(/<[^>]+>/g, "")
                          .slice(0, 180)}
                        {String(blog.content || "").length > 180 ? "..." : ""}
                      </p>
                      
                      <Link to={`/blogs/${blog.id}`} className="luxury-card__link">
                        Read Article <FaArrowRight />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            )}
          </Container>
        </section>

        {/* Topics Section */}
        <section className="luxury-section">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaGem /> Explore Topics
              </div>
              <h2 className="luxury-section__title">Dive Deeper into Luxury Jewellery</h2>
              <p className="luxury-section__subtitle">
                Discover comprehensive guides and insights across all aspects of Ethiopian gemstone jewellery.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--4">
              {blogTopics.map((topic, index) => (
                <div key={index} className="luxury-card">
                  <div className="luxury-card__body">
                    <div className="luxury-card__badge" style={{ position: 'relative', top: 0, left: 0, marginBottom: '1rem' }}>
                      {topic}
                    </div>
                    <p className="luxury-card__description">
                      Explore in-depth articles, guides, and stories about {topic.toLowerCase()} in Ethiopian luxury jewellery.
                    </p>
                    <Link to={`/blogs?topic=${topic.toLowerCase().replace(/\s+/g, '-')}`} className="luxury-card__link">
                      Browse Articles <FaArrowRight />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Newsletter CTA */}
        <section className="luxury-section luxury-section--dark">
          <Container>
            <div className="luxury-section__heading">
              <h2 className="luxury-section__title">Stay Updated with Luxury Insights</h2>
              <p className="luxury-section__subtitle">
                Subscribe to our journal for exclusive articles, gemstone discoveries, and luxury jewellery trends.
              </p>
            </div>
            
            <div className="text-center">
              <form className="d-flex justify-content-center gap-2" style={{ maxWidth: '500px', margin: '0 auto' }}>
                <input
                  type="email"
                  className="form-control luxury-ai-prompt__textarea"
                  placeholder="Your email address"
                  style={{ flex: 1 }}
                />
                <button type="submit" className="wc-button wc-button-primary">
                  Subscribe
                </button>
              </form>
              <p className="text-muted mt-3" style={{ fontSize: '0.85rem' }}>
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default Blog;